import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import { useAvatar } from "../../context/AvatarContext";
import {
  fetchMeProfile,
  fetchMyBadges,
  fetchMyHistory,
} from "../../api/profileService";

const ProfileHubContext = createContext(null);

function buildBadgeColor(typeKey) {
  if (typeKey === "badge-challenger") {
    return "bg-amber-100 text-amber-700 border-amber-200";
  }

  return "bg-slate-100 text-slate-700 border-slate-200";
}

export function ProfileHubProvider({ children }) {
  const { user, isAuthenticated, updateUser } = useAuth();
  const {
    avatarCatalog,
    badgeCatalog,
    currentAvatar,
    currentProfile,
    unlockedAvatars,
    lockedAvatars,
    missionProgress,
    setActiveAvatar,
    unlockedBadges,
    syncBackendBadgeUnlocks,
  } = useAvatar();

  const [profileStats, setProfileStats] = useState(null);
  const [backendBadges, setBackendBadges] = useState([]);
  const [backendAvailableBadges, setBackendAvailableBadges] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    profile: false,
    badges: false,
    history: false,
  });

  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }

    const nextProfile = await fetchMeProfile();
    setProfileStats(nextProfile);
    updateUser({
      avatar: nextProfile?.avatar || null,
      avatar_status: nextProfile?.avatar_status || "approved",
    });
    setErrors((current) => ({
      ...current,
      profile: false,
    }));
    return nextProfile;
  }, [isAuthenticated, updateUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfileStats(null);
      setBackendBadges([]);
      setBackendAvailableBadges([]);
      setHistory([]);
      setLoading(false);
      setErrors({
        profile: false,
        badges: false,
        history: false,
      });
      return;
    }

    const profileController = new AbortController();
    const badgesController = new AbortController();
    const historyController = new AbortController();

    setLoading(true);

    Promise.allSettled([
      fetchMeProfile(profileController.signal),
      fetchMyBadges(badgesController.signal),
      fetchMyHistory(historyController.signal),
    ]).then(([profileResult, badgesResult, historyResult]) => {
      const nextProfileStats =
        profileResult.status === "fulfilled" ? profileResult.value : null;
      setProfileStats(nextProfileStats);
      if (nextProfileStats) {
        updateUser({
          avatar: nextProfileStats.avatar || null,
          avatar_status: nextProfileStats.avatar_status || "approved",
        });
      }

      if (badgesResult.status === "fulfilled") {
        const nextBadges = Array.isArray(badgesResult.value?.badges)
          ? badgesResult.value.badges
          : [];
        const nextAvailableBadges = Array.isArray(
          badgesResult.value?.available_badges
        )
          ? badgesResult.value.available_badges
          : [];
        setBackendBadges(nextBadges);
        setBackendAvailableBadges(nextAvailableBadges);
        syncBackendBadgeUnlocks(nextBadges);
      } else {
        setBackendBadges([]);
        setBackendAvailableBadges([]);
      }

      setHistory(
        historyResult.status === "fulfilled" &&
          Array.isArray(historyResult.value?.results)
          ? historyResult.value.results
          : []
      );

      setErrors({
        profile: profileResult.status !== "fulfilled",
        badges: badgesResult.status !== "fulfilled",
        history: historyResult.status !== "fulfilled",
      });
      setLoading(false);
    });

    return () => {
      profileController.abort();
      badgesController.abort();
      historyController.abort();
    };
  }, [isAuthenticated, syncBackendBadgeUnlocks, updateUser]);

  const displayBadges = useMemo(() => {
    if (backendBadges.length > 0) {
      const nextByFamily = new Map();
      backendAvailableBadges.forEach((badge) => {
        const typeKey = badge.badge_type || badge.condition_type || "badge";
        const categoryKey = badge.category_slug || badge.category || "";
        const key = `${badge.family || ""}|${typeKey}|${categoryKey}|${Number(badge.level || 0)}`;
        nextByFamily.set(key, badge);
      });

      return backendBadges.map((badge) => {
        const localBadgeId = badge.avatar_id || `badge-${badge.id}`;
        const localBadge = badgeCatalog.find((item) => item.id === localBadgeId);
        const typeKey = badge.badge_type || badge.condition_type || "badge";
        const categoryKey = badge.category_slug || badge.category || "";
        const nextKey = `${badge.family || ""}|${typeKey}|${categoryKey}|${Number(badge.level || 0) + 1}`;
        const nextBadge = nextByFamily.get(nextKey) || null;

        return {
          id: localBadgeId,
          dbId: badge.id,
          name: badge.name,
          description: badge.description,
          icon: badge.icon_image || badge.icon || localBadge?.icon || "*",
          color: localBadge?.color || buildBadgeColor(localBadgeId),
          unlocked_at: badge.unlocked_at,
          condition_type: badge.condition_type,
          condition_value: badge.condition_value,
          current_count: badge.current_count ?? null,
          next_condition_value: nextBadge?.condition_value ?? null,
          family: badge.family,
          badge_type: badge.badge_type,
          category_slug: badge.category_slug,
          level: badge.level,
        };
      }).sort((left, right) => {
        const familyOrder = String(left.family || "").localeCompare(String(right.family || ""));
        if (familyOrder) return familyOrder;
        const typeOrder = String(left.badge_type || left.condition_type || "").localeCompare(
          String(right.badge_type || right.condition_type || "")
        );
        if (typeOrder) return typeOrder;
        return Number(left.level || 0) - Number(right.level || 0);
      });
    }

    return unlockedBadges.map((badge) => ({
      ...badge,
      icon: badge.icon || "*",
    }));
  }, [backendAvailableBadges, backendBadges, badgeCatalog, unlockedBadges]);

  const lockedBadges = useMemo(() => {
    const unlockedIds = new Set(displayBadges.map((badge) => badge.id));
    if (backendAvailableBadges.length > 0) {
      return backendAvailableBadges
        .filter((badge) => !badge.unlocked)
        .map((badge) => {
          const localBadgeId = badge.avatar_id || `badge-${badge.id}`;
          const localBadge = badgeCatalog.find((item) => item.id === localBadgeId);

          return {
            id: localBadgeId,
            dbId: badge.id,
            name: badge.name,
            description: badge.description,
            icon: badge.icon_image || badge.icon || localBadge?.icon || "*",
            color: localBadge?.color || buildBadgeColor(localBadgeId),
            condition_type: badge.condition_type,
            condition_value: badge.condition_value,
            current_count: badge.current_count ?? null,
            family: badge.family,
            level: badge.level,
          };
        });
    }

    return badgeCatalog
      .filter((badge) => !unlockedIds.has(badge.id))
      .map((badge) => ({
        ...badge,
        icon: badge.icon || "*",
      }));
  }, [backendAvailableBadges, badgeCatalog, displayBadges]);

  const profileIdentity = useMemo(
    () => ({
      pseudo:
        profileStats?.pseudo || user?.username || currentProfile.pseudo || "Joueur",
      id: profileStats?.id || user?.id || null,
      avatar:
        profileStats?.avatar
          ? { image: profileStats.avatar, type: "backend", name: "Avatar profil" }
          : currentAvatar,
      total_score: Number(profileStats?.total_score || 0),
      global_score: Number(profileStats?.global_score || profileStats?.total_score || 0),
      category_scores: Array.isArray(profileStats?.category_scores)
        ? profileStats.category_scores.map((cs) => ({
            category: cs.category,
            category_name: cs.category_name,
            score: cs.score,
            games_played: cs.games_played || 0,
            avg_rank: cs.avg_rank ?? null,
            best_sessions: Array.isArray(cs.best_sessions) ? cs.best_sessions : [],
            best_game: cs.best_game || null,
          }))
        : [],
      games_played: Number(profileStats?.games_played || 0),
      best_score: Number(profileStats?.best_score || 0),
      average_score: Number(profileStats?.average_score || 0),
      email: profileStats?.email || user?.email || "",
      first_name: profileStats?.first_name || user?.first_name || "",
      last_name: profileStats?.last_name || user?.last_name || "",
      country: profileStats?.country || "",
      is_premium: Boolean(profileStats?.is_premium || user?.is_premium),
      is_moderator: Boolean(profileStats?.is_moderator),
      is_admin: Boolean(profileStats?.is_admin),
      likes_count: Number(profileStats?.likes_count || 0),
      last_login: profileStats?.last_login || null,
      city: profileStats?.city || "",
      birth_year: profileStats?.birth_year || null,
      subscription: profileStats?.subscription || null,
      featured_badge: profileStats?.featured_badge || null,
      bio: profileStats?.bio || "",
      public_slug: profileStats?.public_slug || "",
      best_game_snapshots: Array.isArray(profileStats?.best_game_snapshots)
        ? profileStats.best_game_snapshots
        : [],
      is_public: Boolean(profileStats?.is_public !== false),
      avatar_status: profileStats?.avatar_status || "approved",
      profile_background: profileStats?.profile_background || "",
      profile_background_status: profileStats?.profile_background_status || "approved",
      team: profileStats?.team || null,
      total_points: Number(profileStats?.total_points || 0),
      global_rank: profileStats?.global_rank ?? null,
    }),
    [currentAvatar, currentProfile.pseudo, profileStats, user]
  );

  const historyItems = useMemo(
    () =>
      history.map((item) => ({
        ...item,
        categoryLabel: item.category || "Categorie",
        formattedDate: item.created_at
          ? new Date(item.created_at).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-",
      })),
    [history]
  );

  const summary = useMemo(() => {
    const wins = historyItems.filter((item) => Number(item.position) === 1).length;
    const rankedGames = historyItems.filter((item) => item.is_ranked).length;
    const bestFinish = historyItems.reduce((best, item) => {
      const nextPosition = Number(item.position || 0);
      if (!nextPosition) return best;
      if (!best || nextPosition < best) return nextPosition;
      return best;
    }, 0);

    const categoryGameCounts = historyItems.reduce((accumulator, item) => {
      const key = item.category || item.categoryLabel || "Autre";
      accumulator[key] = (accumulator[key] || 0) + 1;
      return accumulator;
    }, {});

    const topCategories = profileIdentity.category_scores
      .map((item) => ({
        category: item.category_name || item.category || "Autre",
        categorySlug: item.category,
        games: categoryGameCounts[item.category] || 0,
        totalScore: Number(item.score || 0),
        bestScore: Number(item.score || 0),
      }))
      .sort((left, right) => right.totalScore - left.totalScore);

    return {
      wins,
      rankedGames,
      bestFinish,
      topCategories,
      recentHistory: historyItems.slice(0, 5),
      recentBadges: displayBadges.slice(0, 3),
    };
  }, [displayBadges, historyItems, profileIdentity.category_scores]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading: loading,
      errors,
      profileIdentity,
      avatarCatalog,
      currentAvatar: profileIdentity.avatar,
      currentProfile,
      unlockedAvatars,
      lockedAvatars,
      displayBadges,
      lockedBadges,
      missionProgress,
      setActiveAvatar,
      historyItems,
      summary,
      refreshProfile,
    }),
    [
      avatarCatalog,
      currentProfile,
      displayBadges,
      errors,
      historyItems,
      isAuthenticated,
      loading,
      lockedAvatars,
      lockedBadges,
      missionProgress,
      profileIdentity,
      refreshProfile,
      setActiveAvatar,
      summary,
      unlockedAvatars,
    ]
  );

  return (
    <ProfileHubContext.Provider value={value}>
      {children}
    </ProfileHubContext.Provider>
  );
}

export function useProfileHub() {
  return useContext(ProfileHubContext);
}
